import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, RefreshCcw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompanyDetail } from "@/components/companies/CompanyDetail";
import { CallLogForm } from "@/components/activity/CallLogForm";
import { RescoreDialog } from "@/components/companies/RescoreDialog";
import type { AttachmentZoneHandle } from "@/components/attachments/AttachmentZone";
import { useAttachments } from "@/hooks/useAttachments";
import { useAuthStore } from "@/stores/auth.store";
import { useCallTimer } from "@/stores/callTimer.store";
import type { NotesEditorHandle } from "@/components/companies/NotesEditor";
import { getCompany } from "@/api/companies";
import { getTimeline } from "@/api/activity";
import { getOutreach } from "@/api/outreach";
import { listMembers } from "@/api/projects";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import type {
  AttachmentRead,
  CompanyDetail as CompanyDetailType,
  OutreachStatus,
  ProjectMember,
  ScoringRule,
} from "@/api/types";
import { cn } from "@/lib/utils";
import { cameFromTask, type FromTask } from "@/lib/taskReturn";
import type { OutreachLoadStatus } from "@/components/companies/OutreachSection";
import {
  getEffectivePipelineStatuses,
  getEffectiveScoringRules,
} from "@/lib/projectConfig";

type LoadStatus = "loading" | "ok" | "error";

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const slug = project?.slug ?? null;
  const companyId = id ? Number(id) : NaN;
  // Opened from a task: "back" returns to it, its drawer open again, or to
  // the dashboard row that listed it (Prism task #270). The previous history
  // entry is that page.
  const fromTask = cameFromTask(useLocation().state);
  const goBack = () =>
    fromTask ? navigate(-1) : navigate(`/projects/${slug}/companies`);

  const [company, setCompany] = useState<CompanyDetailType | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [callOpen, setCallOpen] = useState(false);
  const [rescoreOpen, setRescoreOpen] = useState(false);
  const notesRef = useRef<NotesEditorHandle>(null);
  // The call-timer store stages a `pendingLog` payload when the user
  // stops a timer. We auto-open the LogCallDialog *here* — pre-filled
  // with the timer's snapshot — when the pending company matches the
  // one being viewed, then clear the staging slot when the dialog
  // resolves (submit or cancel).
  const pendingLog = useCallTimer((s) => s.pendingLog);
  const clearPendingLog = useCallTimer((s) => s.clearPendingLog);
  const pendingForThisCompany =
    pendingLog &&
    pendingLog.companyId === Number(id) &&
    pendingLog.contactId != null
      ? pendingLog
      : null;
  const callPrefill = pendingForThisCompany
    ? {
        contactId: pendingForThisCompany.contactId as number,
        contactName: pendingForThisCompany.contactName,
        durationMinutes: pendingForThisCompany.durationMinutes,
        startedAt: new Date(pendingForThisCompany.startedAtMs),
        endedAt: new Date(pendingForThisCompany.endedAtMs),
      }
    : null;
  const callDialogOpen = callOpen || pendingForThisCompany !== null;

  // ─── Outreach (independent, non-critical) ──────────────────────────────
  // The Lemlist round-trip can take 1–3 s and is allowed to fail without
  // affecting the rest of the page. Kept fully separate from the company
  // fetch so an error here can't take down the page.
  const [outreach, setOutreach] = useState<OutreachStatus | null>(null);
  const [outreachStatus, setOutreachStatus] =
    useState<OutreachLoadStatus>("idle");

  const refetchOutreach = useCallback(async () => {
    if (!slug || Number.isNaN(companyId)) return;
    setOutreachStatus("loading");
    try {
      const next = await getOutreach(slug, companyId);
      setOutreach(next);
      setOutreachStatus("ok");
    } catch (err) {
      console.warn("Failed to load outreach data:", err);
      setOutreach(null);
      setOutreachStatus("error");
    }
  }, [slug, companyId]);

  const applyPartialUpdate = useCallback(
    (updates: Partial<CompanyDetailType>) => {
      setCompany((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    [],
  );

  const refreshActivities = useCallback(async () => {
    if (!slug || Number.isNaN(companyId)) return;
    try {
      const res = await getTimeline(slug, companyId);
      setCompany((prev) =>
        prev ? { ...prev, activities: res.items } : prev,
      );
    } catch {
      // Silent — the next user action or full refetch will sync.
    }
  }, [slug, companyId]);

  // ─── Company fetch (PRIMARY, critical) ────────────────────────────────
  // Owns its own cancelled flag so a stale response (e.g. user navigated
  // to another company before this one resolved) can't overwrite the
  // current state. Failure here is the only one that puts the page in an
  // error state.
  useEffect(() => {
    if (!slug || Number.isNaN(companyId)) return;
    let cancelled = false;
    setStatus("loading");
    setErrorMessage(null);

    getCompany(slug, companyId)
      .then((data) => {
        if (cancelled) return;
        setCompany(data);
        setStatus("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setErrorMessage("Company not found.");
        } else {
          setErrorMessage(extractErrorMessage(err));
        }
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [slug, companyId]);

  // ─── Outreach fetch (PHASE 3 — slow, deferred) ────────────────────────
  // The Lemlist round-trip on the backend is 1–3 s and is *not* allowed
  // to slow down the rest of the page. Two rules govern it:
  //
  //  1. Reset outreach state to "idle" the moment the user navigates to a
  //     different company, so the section shows its skeleton during the
  //     wait instead of the previous company's data.
  //  2. Defer the fetch until Phase 1 (getCompany) has resolved with
  //     status "ok". This guarantees the rest of the page is already
  //     painted before we even start the slow request — we never burn
  //     the network slot on the slow call ahead of the critical one.
  //
  // A failure here only affects the outreach section (skeleton →
  // "Unable to load outreach data" + Retry); the rest of the page is
  // intact.
  useEffect(() => {
    setOutreach(null);
    setOutreachStatus("idle");
  }, [slug, companyId]);

  useEffect(() => {
    if (status !== "ok") return;
    if (!slug || Number.isNaN(companyId)) return;
    let cancelled = false;
    setOutreachStatus("loading");

    getOutreach(slug, companyId)
      .then((data) => {
        if (cancelled) return;
        setOutreach(data);
        setOutreachStatus("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Failed to load outreach data:", err);
        setOutreach(null);
        setOutreachStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [status, slug, companyId]);

  // ─── Members (independent, non-critical) ──────────────────────────────
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => {
        if (!cancelled) setMembers(m);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ─── Attachments (independent, non-critical) ──────────────────────────
  // Lives at the page-shell level (NOT inside CompanyDetail) so the
  // page-wide drag overlay can route into the same upload pipeline.
  // The hook is dormant while `company` is null (entityId: null), so
  // it costs nothing during the initial loading state.
  //
  // TODO: migrate to layout customizer field — see Phase D in
  // attachments roadmap. For now the section ships as a top-level
  // Card outside the customizer system so we can unblock Phase A
  // without touching the 2500-line CompanyDetail.tsx.
  const currentUser = useAuthStore((s) => s.user);
  const attachments = useAttachments({
    slug,
    entityType: "company",
    entityId: company?.id ?? null,
  });
  const attachmentZoneRef = useRef<AttachmentZoneHandle | null>(null);

  /** Per-row delete gate: the uploader can always remove their own
   *  file; an admin (canEdit on Companies covers this) can remove
   *  anyone's; superadmin is implicitly granted via canEdit. The
   *  uploader_user_id column is nullable (uploader deleted) — in
   *  that case only admins can clean up the orphan. */
  const canRemoveAttachment = useCallback(
    (att: AttachmentRead) => {
      if (perms.canEdit) return true;
      if (!currentUser || !att.uploader_user_id) return false;
      return att.uploader_user_id === currentUser.id;
    },
    [perms.canEdit, currentUser],
  );

  // ─── Page-wide drag-and-drop ──────────────────────────────────────────
  // Counter-tracked drag handlers on the page shell. When the user
  // drags anything (from desktop, from another tab, etc.) over the
  // CompanyDetail body, we light up a full-page overlay and route
  // the drop into the same validation/upload pipeline the
  // AttachmentZone uses internally. The pattern protects us from the
  // dragenter/leave-on-child flicker that a naive boolean would suffer.
  const [pageDragActive, setPageDragActive] = useState(false);
  const pageDragCounterRef = useRef(0);

  // Only enable page-wide D&D once the company is loaded AND the
  // viewer is allowed to upload — Viewers (no canEdit) shouldn't see
  // the overlay light up at all.
  const pageDragEnabled = !!company && perms.canEdit;

  const onPageDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!pageDragEnabled) return;
      // Only react to file drags — text/HTML drags inside the page
      // (e.g. a selected note range) shouldn't trigger the overlay.
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      pageDragCounterRef.current += 1;
      if (pageDragCounterRef.current === 1) setPageDragActive(true);
    },
    [pageDragEnabled],
  );
  const onPageDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!pageDragEnabled) return;
      e.preventDefault();
      pageDragCounterRef.current = Math.max(
        0,
        pageDragCounterRef.current - 1,
      );
      if (pageDragCounterRef.current === 0) setPageDragActive(false);
    },
    [pageDragEnabled],
  );
  const onPageDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!pageDragEnabled) return;
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
    },
    [pageDragEnabled],
  );
  const onPageDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      if (!pageDragEnabled) return;
      e.preventDefault();
      pageDragCounterRef.current = 0;
      setPageDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      // Route into AttachmentZone's imperative handler so all
      // validation (mime / size / cap) + the toast pipeline lives
      // in exactly one place.
      await attachmentZoneRef.current?.handleFiles(files);
    },
    [pageDragEnabled],
  );

  const attachmentTitle = useMemo(() => {
    if (!company) return "Attachments";
    return `Attachments · ${company.name ?? "Company"}`;
  }, [company]);

  // Used by enroll/unenroll/etc. to manually trigger a full refetch of the
  // company state. Kept as a stable callback so the toolbar buttons can
  // pass it as `onChanged`.
  const reloadCompany = useCallback(async () => {
    if (!slug || Number.isNaN(companyId)) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const data = await getCompany(slug, companyId);
      setCompany(data);
      setStatus("ok");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setErrorMessage("Company not found.");
      } else {
        setErrorMessage(extractErrorMessage(err));
      }
      setStatus("error");
    }
  }, [slug, companyId]);

  if (status === "loading" || !company) {
    return (
      <div className="space-y-4">
        <Header slug={slug} fromTask={fromTask} onBack={goBack} />
        <div className="flex items-center gap-3 rounded-plane bg-white p-6 text-sm text-slate-500 shadow-plane">
          {status === "error" ? (
            <>
              <span className="text-rose-600">{errorMessage}</span>
              <Button variant="outline" size="sm" onClick={reloadCompany}>
                <RefreshCcw className="mr-2 h-3.5 w-3.5" />
                Retry
              </Button>
            </>
          ) : (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading company…
            </>
          )}
        </div>
      </div>
    );
  }

  const pipelineStatuses = getEffectivePipelineStatuses(project);
  const scoringRules = getEffectiveScoringRules(project) as Array<
    ScoringRule | Record<string, unknown>
  >;

  return (
    <div
      // Page-wide drag-and-drop: any file dragged over the company
      // detail body lights up the overlay and drops into the
      // AttachmentZone's pipeline. Scoped to this wrapper so the
      // sidebar / global header are unaffected.
      onDragEnter={onPageDragEnter}
      onDragLeave={onPageDragLeave}
      onDragOver={onPageDragOver}
      onDrop={onPageDrop}
      className="relative"
    >
      <Header slug={slug} fromTask={fromTask} onBack={goBack} />

      <CompanyDetail
        company={company}
        pipelineStatuses={pipelineStatuses}
        scoringRules={scoringRules}
        members={members}
        projectSlug={slug ?? ""}
        notesRef={notesRef}
        onChanged={reloadCompany}
        onCompanyPatched={applyPartialUpdate}
        onRefreshActivities={refreshActivities}
        onArchived={() => navigate(`/projects/${slug}/companies`)}
        outreach={outreach}
        outreachStatus={outreachStatus}
        onOutreachData={setOutreach}
        onOutreachRefetch={refetchOutreach}
        onRescoreClick={
          perms.canRescore ? () => setRescoreOpen(true) : undefined
        }
        onLogCallClick={
          perms.canLogCall ? () => setCallOpen(true) : undefined
        }
        onNoteClick={
          perms.canEditNotes
            ? () => {
                // The double rAF gives React time to commit the
                // editing state and the motion.div time to mount
                // before we measure the scroll target — without
                // it, scroll jumps to where the collapsed "Add a
                // note…" button used to be and the expanded
                // editor pushes past the viewport.
                notesRef.current?.startEdit();
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    document
                      .getElementById("company-notes-editor")
                      ?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                  });
                });
              }
            : undefined
        }
        // TODO: migrate to layout customizer field — see Phase D in
        // attachments roadmap. The slot threads the page-owned
        // hook + handlers + imperative ref into the column that
        // hosts Notes, so the card renders immediately below
        // Notes (whichever column the user has placed Notes in)
        // without entering the customizer registry.
        attachmentsSlot={{
          attachments: attachments.attachments,
          isLoading: attachments.isLoading && !attachments.loaded,
          cap: attachments.cap,
          upload: attachments.upload,
          remove: attachments.remove,
          download: attachments.download,
          canUpload: perms.canEdit,
          canRemove: canRemoveAttachment,
          attachmentZoneRef,
          title: attachmentTitle,
          // Wire image thumbnails — the Company page is a browsing
          // surface (high cap, users scan the list), so eager-
          // prefetch the first 10 image rows. The remaining rows
          // lazy-load via IntersectionObserver inside AttachmentZone.
          imageThumbnails: {
            slug: slug ?? "",
            entityType: "company",
            entityId: company.id,
          },
          prefetchImages: true,
        }}
      />

      {/* Action bar lives inside CompanyDetail now — it shares
       *  one floating container with the layout-edit and unsaved
       *  state bars (mode="wait" cross-fade between the three).
       *  Rescore / Log call dialogs stay here because they own
       *  the page-level state; the action bar passes handlers
       *  down. */}

      {/* Page-wide drop overlay — only mounted while a file drag is
       *  actively over the page, so it can't capture clicks when
       *  idle. Pointer events are off on the inner content; the
       *  outer wrapper already owns onDrop. */}
      {pageDragActive && (
        <div
          className={cn(
            "pointer-events-none fixed inset-0 z-40 flex items-center justify-center",
            "bg-indigo-500/10 backdrop-blur-[1px]",
          )}
          aria-hidden
        >
          <div className="flex flex-col items-center gap-2 rounded-plane border-2 border-dashed border-indigo-400 bg-white/90 px-8 py-6 text-indigo-700 shadow-elevated">
            <Upload className="h-6 w-6" />
            <span className="text-sm font-medium">
              Drop to attach to this company
            </span>
            <span className="text-xs text-indigo-500">
              PDF, DOCX, XLSX, PPTX, JPG, PNG · up to 25 MB
            </span>
          </div>
        </div>
      )}

      {slug && (
        <CallLogForm
          open={callDialogOpen}
          onOpenChange={(o) => {
            if (!o) {
              setCallOpen(false);
              // Clearing the pending log here covers both submit
              // (the form closes itself after a successful log) and
              // cancel (the user dismissed without logging — we
              // still drop the staged payload so it doesn't pop the
              // dialog back open on next render).
              if (pendingForThisCompany) clearPendingLog();
            } else {
              setCallOpen(true);
            }
          }}
          projectSlug={slug}
          company={company}
          pipelineStatuses={pipelineStatuses}
          onLogged={() => {
            reloadCompany();
            if (pendingForThisCompany) clearPendingLog();
          }}
          prefill={callPrefill}
        />
      )}

      {slug && (
        <RescoreDialog
          open={rescoreOpen}
          onOpenChange={setRescoreOpen}
          projectSlug={slug}
          companyId={company.id}
          currentScore={company.score}
          onSuccess={reloadCompany}
        />
      )}
    </div>
  );
}

function Header({
  slug,
  fromTask,
  onBack,
}: {
  slug: string | null;
  fromTask: FromTask | null;
  onBack: () => void;
}) {
  if (fromTask) {
    return (
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="min-w-0 max-w-full text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5 shrink-0" />
          {fromTask.back}
          <span className="ml-1.5 min-w-0 truncate font-normal text-slate-400">
            {fromTask.title}
          </span>
        </Button>
      </div>
    );
  }
  return (
    <div className="mb-4 flex items-center gap-2 text-sm">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={!slug}
        className="text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        <Link to={slug ? `/projects/${slug}/companies` : "#"}>Companies</Link>
      </Button>
    </div>
  );
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
